/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import root from 'window-or-global';
import React, { useCallback, useEffect, useRef } from 'react';
import { useLabels } from '@/hooks/useLabels';
import styles from './styles.module.scss';
import {
  ChevronRight,
  Close,
  Icon,
  Text,
  Button,
  View,
  Actionable,
  TagIcon,
  CheckmarkCircle,
  useNotification,
} from '@az/starc-ui';
import logger from '@/utils/logger';
import { localStorage } from '@/utils/localStorage';
import { usePostAcknowldegeMutation } from '@/features/rewards/api/postRewardsAcknowledge';
import { useModalOPPromptContext } from '@/components/ModalOPPromptContext';
import { useUpdatePreferences } from '@/features/myProfile/api/putUpdatePreferences';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { EVENT_TYPE } from '../constants/analytics';

const labelMap = {
  lblSignupButton: 'label_email_signup_button',
  lblReceiveExclusiveOffer: 'label_receive_exclusive_offer',
  lblExpiresIn: 'label_expires_in',
  lblValidToSTH: 'label_valid_to_ship_to_home',
  lblSeeDetails: 'label_see_details',
  lblPromoCodeCopied: 'label_email_promo_code_copied',
  lbluseExclusiveOfferPart1: 'label_use_exclusive_offer_part1',
  lbluseExclusiveOfferPart2: 'label_use_exclusive_offer_part2',
  lblpromoCode1: 'label_email_promo_code1',
  lblpromoCode2: 'label_email_promo_code2',
  lblseeDetails: 'label_see_details',
  lblPromoDiscount1: 'label_email_promo_discount1',
  lblPromoDiscount2: 'label_email_promo_discount2',
  lblPromoDiscountText1: 'label_email_promo_discount_text1',
  lblPromoDiscountText2: 'label_email_promo_discount_text2',
  lblPromoValue1: 'label_email_promo_value1',
  lblPromoValue2: 'label_email_promo_value2',
  lblGet: 'label_get',
  lblOrder: 'label_order',
  lblYouUnlocked: 'label_you_unlocked',
  lblOff: 'label_off',
  lblClosePromotion: 'label_close_promotion',
  lblTogglePromotion: 'label_toggle_promotion',
  lblPromoExpirationDate1: 'label_promo_expiration_date1',
  lblPromoExpirationDate2: 'label_promo_expiration_date2',
  lblAt: 'label_promotext_at',
};

/**
 * Renders the expiration date text for a promotion
 */
type ExpirationDateProps = {
  expirationDate?: string;
  expiresInLabel: string;
};

const ExpirationDate: React.FC<ExpirationDateProps> = ({ expirationDate, expiresInLabel }) => (
  <Text variant="subtitle-regular" className={styles.promoExpireDate}>
    {expiresInLabel} {expirationDate}.
  </Text>
);

export function EmailSideTab() {
  const {
    promoState,
    updatePromoState,
    emailPromptAlertType,
    shouldHideComponent,
    offer,
    promoPrint,
  } = useModalOPPromptContext();

  const labels = useLabels(labelMap);
  const { show } = useNotification();
  const emailSideTab = useRef<HTMLDivElement>(null);

  const handleConfirmPromoView = () => {
    updatePromoState({
      isOpen: true,
      isClosed: false,
      isConfirmed: true,
    });
  };
  const { mutate: updatePreferences, isPending } = useUpdatePreferences({
    onSuccess: handleConfirmPromoView,
  });

  const { mutate: acknowledgementMutation } = usePostAcknowldegeMutation({});

  const handlePostRequest = () => acknowledgementMutation({ alertType: 'OP' });

  const trackClick = () => {
    const { pageType, pageName, siteSection } = root.utag_data || {};

    clickTrack({
      customLinkName: pageType + '-' + EVENT_TYPE,
      eventType: EVENT_TYPE,
      promoEmailOffersMessage: offer?.discount,
      pageName,
      pageType,
      siteSection,
    });
  };

  const handleTogglePromo = useCallback(() => {
    updatePromoState((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  }, [updatePromoState]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        emailSideTab.current &&
        e.target instanceof Node &&
        !emailSideTab.current.contains(e.target) &&
        promoState.isOpen
      ) {
        const isVerticalScrollbarClick = isClickOnVerticalScrollbar(e);
        if (!isVerticalScrollbarClick) {
          handleTogglePromo();
        }
      }
    }

    function handleScroll(e: Event) {
      if (promoState.isOpen) {
        const target = e.target as Node;
        const windowWidth = window.innerWidth;

        if (windowWidth < 2420) {
          if (emailSideTab.current && !emailSideTab.current.contains(target)) {
            handleTogglePromo();
          }
        }
      }
    }

    function isClickOnVerticalScrollbar(e: MouseEvent): boolean {
      const windowWidth = document.documentElement.clientWidth;
      const documentWidth = document.documentElement.scrollWidth;
      if (documentWidth > windowWidth && e.clientX > windowWidth - 20) {
        return true;
      }
      return false;
    }
    if (promoState.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [handleTogglePromo, promoState.isOpen]);

  if (shouldHideComponent) return null;

  function handleClosePromo() {
    localStorage.setItem('displayEmailOptInAlert', 'false');
    if (emailPromptAlertType) {
      handlePostRequest();
    }
    updatePromoState({
      isOpen: false,
      isClosed: true,
      isConfirmed: false,
    });
  }

  function handleConfirmSignup() {
    trackClick();
    localStorage.setItem('displayEmailOptInAlert', 'false');
    if (emailPromptAlertType) {
      handlePostRequest();
    }

    updatePreferences({
      receivePromoAndSpecialEvents: true,
      receivePromoPrint: promoPrint,
    });
  }

  async function handleCopyPromoCode() {
    const existingNotification = document.querySelector(`.${styles.promoSnackBar}`);
    if (existingNotification) {
      return;
    }
    try {
      await navigator.clipboard.writeText(offer?.promoCode || '');
      show({
        text: labels.lblPromoCodeCopied,
        type: 'snack-bar',
        className: styles.promoSnackBar,
      });
    } catch (error) {
      logger.error({ message: 'Failed to copy promo code to clipboard', meta: error });
    }
  }

  return (
    <div ref={emailSideTab}>
      <View className={cx(styles.emailSideTab, { [styles.expanded]: promoState.isOpen })}>
        {/* Minimized State */}

        <View className={styles.emailTabMinimized}>
          <View className={styles.emailTabMinimizedContent}>
            <Actionable
              onClick={handleClosePromo}
              className={styles.promoClose}
              attributes={{ 'data-testid': 'close-promo' }}
            >
              <Icon svg={Close} size={5} />
              <p className="sr-only">{labels.lblClosePromotion}</p>
            </Actionable>
            <Actionable
              onClick={handleTogglePromo}
              className={styles.promoToggle}
              attributes={{ 'aria-expanded': promoState.isOpen, 'data-testid': 'promo-toggle' }}
            >
              <Text variant="large-body" color="600" className={styles.promoText}>
                {offer?.discount}
              </Text>
              <Icon
                svg={ChevronRight}
                className={cx(styles.chevronIcon, {
                  [styles.rotateToggle]: promoState.isOpen,
                })}
                size={5}
              />
            </Actionable>
          </View>
        </View>

        {/* Expanded State */}
        {!promoState.isConfirmed && (
          <View className={styles.emailPromoExpanded} aria-hidden={!promoState.isOpen}>
            <View className={styles.promoHeader}>
              <Icon size={{ s: 8, m: 10 }} svg={TagIcon} className={styles.promoIcon} />
              <Text variant="display-4" className={styles.promoHeader}>
                {labels.lblGet} {offer?.discountText}+ {labels.lblOrder}
              </Text>
            </View>
            <View className={styles.promoDetails}>
              <Button
                onClick={handleConfirmSignup}
                aria-label="signup-for-promo"
                loading={isPending}
              >
                {labels.lblSignupButton}
              </Button>
              <Text variant="subtitle-regular" className={styles.promoSubText}>
                {labels.lblReceiveExclusiveOffer}
              </Text>
            </View>
            <View className={styles.promoFooter}>
              <ExpirationDate
                expirationDate={offer?.expirationDate}
                expiresInLabel={labels.lblExpiresIn}
              />
              <Text variant="subtitle-regular" className={styles.promoLegal}>
                {labels.lblValidToSTH}{' '}
                <Actionable className={styles.promoSeeDetails} href="/lp/autozone-savings">
                  {labels.lblSeeDetails}
                </Actionable>
              </Text>
            </View>
          </View>
        )}

        {/* Confirmed State */}
        {offer && promoState.isConfirmed && (
          <View className={styles.emailPromoExpanded} aria-hidden={!promoState.isOpen}>
            <View className={styles.promoHeader}>
              <Icon size={{ s: 8, m: 10 }} svg={CheckmarkCircle} className={styles.promoIcon} />
              <Text variant="display-4" className={styles.promoHeader}>
                {labels.lblYouUnlocked} {offer.value} {labels.lblOff}
              </Text>
            </View>
            <View className={styles.promoDetailsContainer}>
              <Text variant="large-body" className={styles.promoPartOne}>
                {labels.lbluseExclusiveOfferPart1 ?? 'Use code'}{' '}
                <Actionable className={styles.promoCode} onClick={handleCopyPromoCode}>
                  {offer.promoCode}
                </Actionable>{' '}
                {labels.lblAt}
              </Text>
              <Text className={styles.promoParttwo}>{labels.lbluseExclusiveOfferPart2}</Text>
            </View>
            <View className={styles.promoFooter}>
              <ExpirationDate
                expirationDate={offer?.expirationDate}
                expiresInLabel={labels.lblExpiresIn}
              />
              <Text variant="subtitle-regular" className={styles.promoLegal}>
                {labels.lblValidToSTH}{' '}
                <Actionable className={styles.promoSeeDetails} href="/lp/autozone-savings">
                  {labels.lblSeeDetails}
                </Actionable>
              </Text>
            </View>
          </View>
        )}
      </View>
    </div>
  );
}
